import type { Contact } from '@/shared/types/contacts';
import { normalizePhone } from '@/util/validation/patterns';

import { CONTACT_PHONE_SHAPE } from '../constants';

/**
 * Whether `value` is one of the contact's own identifiers (primary email, phone
 * or external id). Emails and phones compare loosely, since the same identity
 * reaches us formatted differently per channel; external ids must match exactly.
 *
 * The phone branch is gated on the term looking like a phone number: stripping
 * non-digits from anything else would let an external id such as `ID-1-202-555-0111`
 * match the phone `+1 202 555 0111`.
 */
export const matchesContactIdentity = (contact: Contact, value: string): boolean => {
  const term = value.trim();
  if (!term) {
    return false;
  }
  const normalizedTerm = term.normalize('NFKC');
  if (
    contact.email &&
    contact.email.trim().normalize('NFKC').toLowerCase() === normalizedTerm.toLowerCase()
  ) {
    return true;
  }
  if (contact.external_id && contact.external_id === term) {
    return true;
  }
  if (!contact.phone || !CONTACT_PHONE_SHAPE.test(normalizedTerm)) {
    return false;
  }
  const phone = normalizePhone(contact.phone);
  return !!phone && phone === normalizePhone(normalizedTerm);
};
